import { LocalStorageAuthGateway } from './local-storage-auth-gateway.js';

const DEFAULT_AUTH_STATE = { isLoggedIn: false, name: '', email: '', id: null };

function cloneDefaultAuth(patch = {}) {
  return { ...DEFAULT_AUTH_STATE, ...patch };
}

const AUTH_STATUS_CACHE_TTL_MS = 5000;

function normalizeCheckOptions(options) {
  if (!options) {
    return { force: false, allowCache: true };
  }
  if (typeof options === 'boolean') {
    return { force: Boolean(options), allowCache: !options };
  }
  return {
    force: Boolean(options.force),
    allowCache: options.allowCache !== false,
  };
}

function toAuthSnapshot(state) {
  if (state && typeof state === 'object') {
    return cloneDefaultAuth(state);
  }
  return cloneDefaultAuth();
}

function ensureLegacyAuthLogger() {
  if (typeof window === 'undefined') {
    return () => {};
  }
  if (typeof window.__agpLogLegacyAuthUsage === 'function') {
    return window.__agpLogLegacyAuthUsage;
  }
  const state = window.__agpLegacyAuthUsage || {};
  const previousSeen = state.seen;
  state.seen =
    previousSeen instanceof Set
      ? previousSeen
      : new Set(Array.isArray(previousSeen) ? previousSeen : []);
  state.queue = Array.isArray(state.queue) ? state.queue : [];
  function flushQueue() {
    if (!state.queue.length) return;
    if (!window.Debug || typeof window.Debug.addMessage !== 'function') {
      return;
    }
    const pending = state.queue.splice(0, state.queue.length);
    pending.forEach((entry) => {
      const level = entry.level || 'warn';
      const category = entry.category || 'legacy';
      window.Debug.addMessage(level, entry.message, category, entry && entry.timestamp);
    });
  }
  function logLegacyAuthUsage(key, message) {
    const id = String(key || message || 'legacy-auth');
    if (state.seen.has(id)) {
      return;
    }
    state.seen.add(id);
    const payload = {
      level: 'warn',
      category: 'legacy',
      message: message || `[legacy-auth] ${id}`,
      timestamp: Date.now(),
    };
    if (window.Debug && typeof window.Debug.addMessage === 'function') {
      flushQueue();
      window.Debug.addMessage(payload.level, payload.message, payload.category, payload.timestamp);
    } else {
      state.queue.push(payload);
    }
  }
  logLegacyAuthUsage.flushQueue = flushQueue;
  state.flushQueue = flushQueue;
  window.__agpLegacyAuthUsage = state;
  window.__agpLogLegacyAuthUsage = logLegacyAuthUsage;
  return logLegacyAuthUsage;
}

const logLegacyAuthUsage = ensureLegacyAuthLogger();

/**
 * auth-gateway.js
 * Provides an adapter that fronts ambient window.Auth APIs while persisting through
 * the existing LocalStorageAuthGateway.
 */
export class AuthGateway {
  constructor({
    ambientAuth = null,
    persistenceGateway = null,
    inactivityMonitor = null,
    sessionClient = null,
  } = {}) {
    this.ambientAuth =
      ambientAuth ?? (typeof window !== 'undefined' ? window.Auth || null : null);
    this.persistence = persistenceGateway || new LocalStorageAuthGateway();
    this.inactivityMonitor = inactivityMonitor || null;
    this.sessionClient = sessionClient || null;
    this._pendingAuthStatus = null;
    this._lastAuthStatus = null;
  }

  load() {
    if (this.persistence?.load) {
      return this.persistence.load();
    }
    return cloneDefaultAuth();
  }

  save(patch = {}) {
    if (this.persistence?.save) {
      return this.persistence.save(patch);
    }
    return cloneDefaultAuth(patch);
  }

  clear() {
    this.persistence?.clear?.();
  }

  getToken() {
    return this.persistence?.getToken?.() ?? null;
  }

  setToken(token) {
    this.persistence?.setToken?.(token);
  }

  setInactivityMonitor(monitor) {
    this.inactivityMonitor = monitor || null;
  }

  startInactivityTracking() {
    if (this.inactivityMonitor) {
      if (typeof this.inactivityMonitor.start === 'function') {
        try {
          return Boolean(this.inactivityMonitor.start());
        } catch (error) {
          return false;
        }
      }
      return false;
    }
    if (this.ambientAuth?.startInactivityTracking) {
      logLegacyAuthUsage(
        'AuthGateway.startInactivityTracking',
        '[legacy-auth] window.Auth.startInactivityTracking delegated to ambient window.Auth; inject an inactivity monitor or remove the ambient dependency.'
      );
      this.ambientAuth.startInactivityTracking();
      return true;
    }
    return false;
  }

  stopInactivityTracking() {
    if (this.inactivityMonitor) {
      if (typeof this.inactivityMonitor.stop === 'function') {
        try {
          return Boolean(this.inactivityMonitor.stop());
        } catch (error) {
          return false;
        }
      }
      return false;
    }
    if (this.ambientAuth?.stopInactivityTracking) {
      logLegacyAuthUsage(
        'AuthGateway.stopInactivityTracking',
        '[legacy-auth] window.Auth.stopInactivityTracking delegated to ambient window.Auth; inject an inactivity monitor or remove the ambient dependency.'
      );
      this.ambientAuth.stopInactivityTracking();
      return true;
    }
    return false;
  }

  resetInactivityTimer() {
    if (this.inactivityMonitor) {
      if (typeof this.inactivityMonitor.reset === 'function') {
        try {
          return Boolean(this.inactivityMonitor.reset());
        } catch (error) {
          return false;
        }
      }
      return false;
    }
    if (this.ambientAuth?.resetInactivityTimer) {
      logLegacyAuthUsage(
        'AuthGateway.resetInactivityTimer',
        '[legacy-auth] window.Auth.resetInactivityTimer delegated to ambient window.Auth; inject an inactivity monitor or remove the ambient dependency.'
      );
      this.ambientAuth.resetInactivityTimer();
      return true;
    }
    return false;
  }

  async checkAuthStatus(options) {
    const { force, allowCache } = normalizeCheckOptions(options);

    if (!force && this._pendingAuthStatus) {
      return this._pendingAuthStatus.then((state) => toAuthSnapshot(state));
    }

    const now = Date.now();
    if (!force && allowCache && this._lastAuthStatus && now - this._lastAuthStatus.timestamp < AUTH_STATUS_CACHE_TTL_MS) {
      return Promise.resolve(toAuthSnapshot(this._lastAuthStatus.state));
    }

    const performCheck = async () => {
      if (this.sessionClient?.checkAuthStatus) {
        try {
          const result = await this.sessionClient.checkAuthStatus({ force, allowCache });
          if (result) {
            return result;
          }
        } catch (error) {
          // fall through to persistence when session client fails
        }
      }

      if (this.persistence?.load) {
        try {
          return this.persistence.load();
        } catch (error) {
          // ignore and continue to ambient fallback
        }
      }

      if (this.ambientAuth?.checkAuthStatus) {
        logLegacyAuthUsage(
          'AuthGateway.checkAuthStatus',
          '[legacy-auth] window.Auth.checkAuthStatus delegated to ambient window.Auth; inject an auth session client instead.'
        );
        return this.ambientAuth.checkAuthStatus(options);
      }

      return cloneDefaultAuth();
    };

    const next = performCheck()
      .catch(() => this.load())
      .then((state) => {
        const snapshot = toAuthSnapshot(state);
        try {
          this.persistence?.save?.(snapshot);
        } catch (error) {
          // ignore persistence write failures
        }
        this._lastAuthStatus = { state: snapshot, timestamp: Date.now() };
        return snapshot;
      });

    this._pendingAuthStatus = next;
    next.finally(() => {
      if (this._pendingAuthStatus === next) {
        this._pendingAuthStatus = null;
      }
    });

    return next.then((state) => toAuthSnapshot(state));
  }

  getAmbient() {
    return this.ambientAuth || null;
  }
}

if (typeof window !== 'undefined') {
  window.AgileProGateways = window.AgileProGateways || {};
  window.AgileProGateways.AuthGateway = AuthGateway;
}




